import { useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { featureAccess } from "../../../permissions/access";
import { BdcBranch } from "../../../types/bdc.type";
import { useUserRole } from "../../../utils/hooks/useUserRole";
import { sortBranches } from "../Branch/branches";
import { useBdcBranches } from "../Branch/useBdcBranches";
import { CARD_SX, TABLE_SX } from "../styles";
import BranchTab from "./BranchTab";
import DcTab from "./DcTab";
import SkuTab from "./SkuTab";
import SequenceTab from "../Invoice/SequenceTab";
import StoreTab from "./StoreTab";

type SettingsTab = "dc" | "branch" | "store" | "sku" | "invoice";
const TABS: { key: SettingsTab; label: string }[] = [
  { key: "dc", label: "DC" },
  { key: "branch", label: "สาขา" },
  { key: "store", label: "ร้าน 7-11" },
  { key: "sku", label: "สินค้า" },
  { key: "invoice", label: "เลขเอกสาร" },
];
const isSettingsTab = (value: string | null): value is SettingsTab =>
  TABS.some((t) => t.key === value);

/**
 * BDC · ตั้งค่า — the masters import maintains (DC, ร้าน, สินค้า) plus the BDC branches. Head office
 * and หัวหน้า BDC edit DC names / bindings and SKUs; MASTER alone registers branches and test mode.
 */
export default function BdcSettings() {
  const role = useUserRole();
  const canWrite = featureAccess.bdcWrite(role);
  const canWriteBranch = featureAccess.bdcBranchWrite(role);
  const [params, setParams] = useSearchParams();
  const tabParam = params.get("tab");
  const tab: SettingsTab = isSettingsTab(tabParam) ? tabParam : "dc";
  const { branches, loading: branchesLoading, reload: reloadBranches, setBranches } = useBdcBranches();

  const onBranchSaved = (saved: BdcBranch) =>
    setBranches((current) =>
      sortBranches([...current.filter((b) => b.company_id !== saved.company_id), saved]),
    );

  return (
    <Box sx={{ padding: "8px 0" }}>
      <Box mb={2}>
        <Typography variant="h6" fontWeight={700}>
          BDC · ตั้งค่า
        </Typography>
        <Typography variant="body2" color="text.secondary">
          DC, ร้าน 7-11 และสินค้า สร้าง/อัปเดตจากไฟล์ที่นำเข้าอัตโนมัติ · DC ใหม่ต้องผูกสาขาก่อนจึงนำเข้าได้
          {!canWrite && " · บัญชีนี้ดูได้อย่างเดียว"}
        </Typography>
      </Box>
      <Card sx={{ ...CARD_SX, ...TABLE_SX }}>
        <Tabs
          value={tab}
          onChange={(_e, value: SettingsTab) => setParams({ tab: value }, { replace: true })}
          sx={{ px: 1, borderBottom: "1px solid #E3E5EA" }}
        >
          {TABS.map((t) => (
            <Tab key={t.key} value={t.key} label={t.label} />
          ))}
        </Tabs>
        {tab === "dc" && <DcTab canWrite={canWrite} branches={branches} onBindingChanged={reloadBranches} />}
        {tab === "branch" && (
          <BranchTab
            branches={branches}
            loading={branchesLoading}
            canWrite={canWriteBranch}
            onSaved={onBranchSaved}
          />
        )}
        {tab === "store" && <StoreTab />}
        {tab === "sku" && <SkuTab canWrite={canWrite} />}
        {tab === "invoice" && <SequenceTab canWrite={featureAccess.bdcInvoiceSequenceWrite(role)} branches={branches} />}
      </Card>
    </Box>
  );
}
